import { Checkbox } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { FormGroup } from '@mui/material';
import CrawlerSection from 'components/commons/CrawlerSection';
import DoneButton from 'components/input/DoneButton';
import { getUrlParam, normalizeUrl } from 'libs/helper/utils';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { SectionTitle, SectionWrapper } from 'resources/styles/CommonStyles';
import { TableHeaderRow, TableRow } from 'resources/styles/CommonTableStyles';
import { Button, InputField, NumberFormat, Row } from 'saladsoft-primitive';
import { OrderCleansingDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import { useStoreWrapper } from 'stores/StoreHelper';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { OverlayStyle } from 'resources/styles/LoadingOverlay';
import { SyncLoader } from 'react-spinners';

const TitleCleansingModal = observer(
  ({
    row,
    direction,
    onApply,
  }: {
    row: OrderCleansingDto;
    direction?: string;

    onApply: (id: number, title: string, applyToAll: boolean, row: OrderCleansingDto) => void;
  }) => {
    const customsTitleRef = useRef<any>(null);
    const customsRetryTitleRef = useRef<any>(null);
    const prevRetry = useRef<boolean>(false);
    const nextRetry = useRef<boolean>(false);
    const currentPage = useRef<number>(0);
    const isDirtyPrev = useRef<boolean>(false);
    const isDirtyNext = useRef<boolean>(false);
    const isDirtyItem = useRef<boolean>(false);
    const previousIdRef = useRef<string | null>(null);
    const nextIdRef = useRef<string | null>(null);

    const { t } = useTranslation();
    const { uiStore } = useStoreWrapper();
    const isRetry = useRef<boolean>(false);
    const [orderCleansing, setOrderCleansing] = useState<OrderCleansingDto | null>(null);
    const [nextId, setNextId] = useState<string | null>(null);
    const [previousId, setPreviousId] = useState<string | null>(null);
    const [customsTitle, setCustomsTitle] = useState<string>('');
    const [applyToAll, setApplyToAll] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>();

    const handleApply = (title?: string, isCustomsTitle?: boolean) => {
      toast.success(t('Apply Success'));
      if (typeof isCustomsTitle === 'undefined') {
        customsTitleRef.current.value = '';
        if (customsRetryTitleRef.current) customsRetryTitleRef.current.value = '';
        setCustomsTitle('');
      }
      setOrderCleansing({ ...orderCleansing, customsTitle: title, status: 'USER_VERIFIED' });
      onApply(orderCleansing?.id as number, title ?? '', applyToAll, row);
      isDirtyItem.current = true;
    };

    const fetchOrderCleansing = (id: number, direction?: 'next' | 'prev') => {
      setLoading(true);
      setOrderCleansing(null);
      setNextId(null);
      setPreviousId(null);

      const startDate = getUrlParam('startDate');
      const endDate = getUrlParam('endDate');
      const status = getUrlParam('status');
      const category = getUrlParam('category');
      const searchFieldName = getUrlParam('searchKey');
      const searchValue = getUrlParam('searchValue');
      const sort = getUrlParam('sort');
      const size = getUrlParam('size');

      ServiceHelper.orderCleansingService
        ?.getOrderCleansing(id, {
          status: status as
            | 'RECEIVED'
            | 'PROCESSING'
            | 'CLEANSED'
            | 'VALIDATION_ERROR'
            | 'USER_VERIFIED'
            | 'TRANSMISSION_FAILED'
            | 'COMPLETED',
          categoryId: category ? Number(category) : undefined,
          startDate: startDate ? startDate : undefined,
          endDate: endDate ? endDate : undefined,
          searchKeyword: searchValue ? searchValue : undefined,
          searchFieldName: searchFieldName ? searchFieldName : undefined,
          sort: sort ? [sort] : undefined,
          size: size ? Number(size) : 50,
          page: currentPage.current,
        })
        .then((res) => {
          if (res.status === 200) {
            const nextId =
              res.headers['x-next-id'] || res.headers['X-Next-Id'] || res.headers['X-NEXT-ID'];
            const previousId =
              res.headers['x-previous-id'] ||
              res.headers['X-Previous-Id'] ||
              res.headers['X-PREVIOUS-ID'];
            // 공통 : nextId나 prevId가 없는경우, 현재 페이지를 가감하아 호출 후, next/prev id를 취득하고
            // 두번 호출에도 없는 경우, 종료한다.
            // debugger;
            //Next 버튼 처리
            if (typeof nextId === 'undefined' && !nextRetry.current && direction === 'next') {
              nextRetry.current = true;
              isDirtyNext.current = true;
              currentPage.current = currentPage.current + 1;
              if (previousId) {
                previousIdRef.current = previousId;
              }
              fetchOrderCleansing(id, 'next');
              return;
            } else {
              if (isDirtyNext.current) {
                if (nextId) {
                  setNextId(nextId);
                }
              } else {
                setNextId(nextId ? nextId : nextIdRef.current);
              }
              nextRetry.current = false;
              isDirtyNext.current = false;
            }

            //Prev 버튼 처리
            if (typeof previousId === 'undefined' && !prevRetry.current && direction === 'prev') {
              prevRetry.current = true;
              isDirtyPrev.current = true;
              currentPage.current = currentPage.current - 1;
              if (nextId) {
                nextIdRef.current = nextId;
              }
              fetchOrderCleansing(id, 'prev');
              return;
            } else {
              if (isDirtyPrev.current) {
                if (previousId) {
                  setPreviousId(previousId);
                }
              } else {
                setPreviousId(previousId ? previousId : previousIdRef.current);
              }
              prevRetry.current = false;
              isDirtyPrev.current = false;
            }

            setOrderCleansing(res.data);
            setOrderCleansing(res.data);
          } else {
            toast.error(t('Failed to fetch order cleansing'));
          }
          setLoading(false);
        });
      setCustomsTitle('');
      setApplyToAll(false);
    };

    const getTableRows = (recommendTitle: string, priority: number) => {
      return (
        <TableRow>
          <td>
            <Row justifyContent="flex-start" alignItems="center" width="100%" gap="20px">
              <Row justifyContent="flex-start">
                <div>{recommendTitle}</div>
                <>
                  {orderCleansing?.customsTitle === recommendTitle ? (
                    <ApplyStyle>{t('Applied')}</ApplyStyle>
                  ) : (
                    <div style={{ marginLeft: 10 }}>
                      <DoneButton
                        onClick={() => {
                          handleApply(recommendTitle);
                        }}
                      />
                    </div>
                  )}
                </>
              </Row>
            </Row>
          </td>
          <td style={{ textAlign: 'center' }}>{priority}</td>
        </TableRow>
      );
    };

    const handleClickClose = () => {
      uiStore?.modal.close();
      if (isDirtyItem.current) {
        window.location.reload();
      }
    };

    useEffect(() => {
      if (row.id) {
        const page = getUrlParam('page');
        currentPage.current = page ? Number(page) - 1 : 0;
        fetchOrderCleansing(row.id, direction as 'next' | 'prev');
        if (
          row.customsTitle != row.recommendTitlePrimary &&
          row.customsTitle != row.recommendTitleSecondary &&
          row.customsTitle != row.recommendTitleTertiary
        ) {
          setCustomsTitle(row.customsTitle ?? '');
        }
      }
    }, [row]);

    useEffect(() => {
      if (orderCleansing) {
        if (
          orderCleansing.customsTitle != orderCleansing.recommendTitlePrimary &&
          orderCleansing.customsTitle != orderCleansing.recommendTitleSecondary &&
          orderCleansing.customsTitle != orderCleansing.recommendTitleTertiary
        ) {
          setCustomsTitle(orderCleansing.customsTitle ?? '');
        }
      }
    }, [orderCleansing]);

    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          uiStore?.modal.close();
        }
      };

      document.addEventListener('keydown', handleEscKey, true);

      return () => {
        document.removeEventListener('keydown', handleEscKey, true);
      };
    }, [uiStore]);

    useEffect(() => {
      const modalContent = document.getElementById('ModalContentStyleBox');
      if (modalContent) {
        const buttons = modalContent.querySelectorAll('button');
        buttons.forEach((button) => {
          button.style.display = 'none';
        });
      }
      return () => {
        isDirtyPrev.current = false;
        isDirtyNext.current = false;
        isDirtyItem.current = false;
        previousIdRef.current = null;
        nextIdRef.current = null;
        setCustomsTitle('');
        setApplyToAll(false);
      };
    }, []);

    useEffect(() => {
      console.log('orderCleansing', orderCleansing);
    }, [orderCleansing]);

    return (
      <>
        <CloseButtonWrapper>
          <IconButtonWrapper onClick={() => handleClickClose()}>
            <CloseIcon />
          </IconButtonWrapper>
        </CloseButtonWrapper>
        <TitleCleansingModalWrapper className="crawler-section-wrapper">
          <Row width="100%" justifyContent="space-between" gap="40px" alignItems="flex-start">
            <div style={{ flex: 0.6 }}>
              <Row justifyContent="space-between" alignItems="center" flexWrap="wrap">
                <SectionInfo>
                  <> {t('RAW DATA')}</>
                </SectionInfo>

                <div style={{ flex: 0.8 }}>
                  <SectionWrapper style={{ marginBottom: 20 }}>
                    <SectionTitle>{t('Title(Origin)')}</SectionTitle>
                    <div>{orderCleansing?.itemName}</div>
                  </SectionWrapper>
                  <SectionWrapper style={{ marginBottom: 20 }}>
                    <div style={{ width: 120 }}>{t('Option')}</div>
                    <div>{orderCleansing?.itemOption || '-'}</div>
                  </SectionWrapper>
                  <Row gap="40px" justifyContent="flex-start">
                    <SectionWrapper style={{ marginBottom: 20 }}>
                      <div style={{ width: 120 }}>{t('Order ID')}</div>
                      <div>{orderCleansing?.orderId || '-'}</div>
                    </SectionWrapper>
                    <SectionWrapper style={{ marginBottom: 20 }}>
                      <div style={{ width: 120 }}>{t('Item Code')}</div>
                      <div>{orderCleansing?.itemCode || '-'}</div>
                    </SectionWrapper>

                    <SectionWrapper style={{ marginBottom: 20 }}>
                      <div style={{ width: 120 }}>{t('Option Code')}</div>
                      <div>{orderCleansing?.optionCode || '-'}</div>
                    </SectionWrapper>

                    <SectionWrapper style={{ marginBottom: 20 }}>
                      <div>{t('HSCode')}</div>
                      <div>{orderCleansing?.hsCode || '-'}</div>
                    </SectionWrapper>
                  </Row>
                  <Row gap="40px" justifyContent="flex-start">
                    <SectionWrapper style={{ marginBottom: 0 }}>
                      <div style={{ width: 120 }}>{t('Unit Price')}</div>
                      <div>
                        {orderCleansing?.unitPrice ? (
                          <NumberFormat value={orderCleansing?.unitPrice ?? 0} />
                        ) : (
                          '-'
                        )}
                      </div>
                    </SectionWrapper>
                    <SectionWrapper style={{ marginBottom: 0 }}>
                      <div style={{ width: 120 }}>{t('Weight')}</div>
                      <div>{orderCleansing?.realWeight || '-'}</div>
                    </SectionWrapper>
                    <SectionWrapper style={{ marginBottom: 0 }}>
                      <div style={{ width: 120 }}>{t('Qty')}</div>
                      <div>{orderCleansing?.quantity || '-'}</div>
                    </SectionWrapper>
                    <SectionWrapper style={{ marginBottom: 0 }}>
                      <div>{t('Total Price')}</div>
                      <div>
                        {orderCleansing?.totalPrice ? (
                          <NumberFormat value={orderCleansing?.totalPrice ?? 0} />
                        ) : (
                          '-'
                        )}
                      </div>
                    </SectionWrapper>
                  </Row>
                  <div style={{ fontSize: 12, color: '#0032e4' }}>{t('notice')}</div>
                </div>
              </Row>
              {/* <>{orderCleansing?.customsTitle}</> */}
              <Divider />
              {orderCleansing?.verificationRetryCount! > -1 &&
                (orderCleansing?.status === 'CLEANSED' ||
                  orderCleansing?.status === 'USER_VERIFIED' ||
                  orderCleansing?.status === 'COMPLETED') && (
                  <Row justifyContent="space-between" alignItems="center" flexWrap="wrap">
                    <SectionInfo style={{ flex: 0.2 }}>{t('AI Suggestion')}</SectionInfo>
                    <div style={{ flex: 0.8 }}>
                      <RoundBoxStyled>
                        <FormGroup>
                          <FormControlLabel
                            control={<Checkbox defaultChecked />}
                            checked={applyToAll}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setApplyToAll(e.target.checked)
                            }
                            label={t('Apply to all items')}
                          />
                        </FormGroup>
                        <div style={{ fontSize: 12, color: '#0032e4', marginLeft: 30 }}>
                          {t('condition')}
                        </div>
                      </RoundBoxStyled>
                      <>
                        <SectionWrapper>
                          <table style={{ width: '100%' }} cellPadding={0} cellSpacing={0}>
                            <thead>
                              <TableHeaderRow>
                                <th style={{ borderBottom: '2px solid #414141' }}>{t('Title')}</th>
                                <th
                                  style={{ borderBottom: '2px solid #414141', textAlign: 'center' }}
                                >
                                  {t('Priority')}
                                </th>
                              </TableHeaderRow>
                            </thead>

                            <tbody>
                              <>{getTableRows(orderCleansing?.recommendTitlePrimary ?? '', 1)}</>
                              <>{getTableRows(orderCleansing?.recommendTitleSecondary ?? '', 2)}</>
                              <>{getTableRows(orderCleansing?.recommendTitleTertiary ?? '', 3)}</>
                            </tbody>
                          </table>

                          <div style={{ width: '100%' }}>
                            <Row gap="10px" justifyContent="flex-start" alignItems="center">
                              <Row gap="10px" flex={1}>
                                <InputField
                                  ref={customsTitleRef}
                                  value={customsTitle}
                                  onChange={(value) => setCustomsTitle(value)}
                                />
                                <Button
                                  style={{ width: 200 }}
                                  isValid={customsTitle.length > 0}
                                  onClick={() => {
                                    handleApply(customsTitle, false);
                                  }}
                                >
                                  {t('Apply')}
                                </Button>
                              </Row>
                            </Row>
                          </div>
                        </SectionWrapper>
                      </>
                    </div>
                  </Row>
                )}
              <>
                {orderCleansing?.status === 'RECEIVED' && (
                  <ReadyWrapper>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <img src="/images/working.gif" alt="working" style={{ width: 100 }} />
                      <div>{t('Item is being analyzed')}</div>
                    </div>
                  </ReadyWrapper>
                )}
              </>
              <>
                {orderCleansing?.verificationRetryCount === -1 ? (
                  <ReadyWrapper>
                    <div
                      style={{
                        position: 'absolute',
                        top: 10,
                        left: 20,
                        right: 0,
                        bottom: 0,
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ fontSize: 20, fontWeight: 600, color: 'red' }}>
                        {t('Verification error')}
                      </div>
                      <div style={{ fontSize: 12, color: '#0032e4' }}>
                        {t('Verification message')}
                      </div>
                    </div>
                    <div style={{ width: '100%' }}>
                      <div style={{ fontSize: 14, marginTop: 30, marginBottom: 5 }}>
                        {t('Cleansing Title')}
                      </div>
                      <Row gap="10px" justifyContent="flex-start" alignItems="center">
                        <Row gap="10px" flex={1}>
                          <InputField
                            ref={customsRetryTitleRef}
                            value={customsTitle}
                            onChange={(value) => setCustomsTitle(value)}
                          />
                          <Button
                            style={{ width: 200 }}
                            onClick={() => {
                              isRetry.current = true;
                              handleApply(customsTitle, false);
                            }}
                            isValid={customsTitle.length > 0}
                          >
                            {t('Apply')}
                          </Button>
                        </Row>
                      </Row>
                    </div>
                  </ReadyWrapper>
                ) : (
                  <></>
                )}
              </>
            </div>
            <div style={{ flex: 0.4 }}>
              <Row gap="10px" justifyContent="flex-start" marginBottom="20px">
                <Button
                  isValid={previousId ? true : false}
                  onClick={() => previousId && fetchOrderCleansing(Number(previousId), 'prev')}
                >
                  {t('Previous')}
                </Button>
                <Button
                  isValid={nextId ? true : false}
                  onClick={() => nextId && fetchOrderCleansing(Number(nextId), 'next')}
                >
                  {t('Next')}
                </Button>
              </Row>
              <CrawlerSection url={normalizeUrl(orderCleansing?.itemUrl ?? '')} />
            </div>
          </Row>
        </TitleCleansingModalWrapper>
        <OverlayStyle show={loading ?? false}>
          <SyncLoader color="#930000" size={10} />
        </OverlayStyle>
      </>
    );
  },
);

const TitleCleansingModalWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 40px;
  position: relative;
`;

const ReadyWrapper = styled.div`
  margin-top: 120px;
  font-size: 16px;
  color: #080808;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
  border: 1px solid #080808;
  border-radius: 10px;
  padding: 20px;
  position: relative;
  flex-direction: column;
  max-width: 550px;
  margin: 0 auto;
`;

const SectionInfo = styled.div`
  font-size: 16px;
  flex: 0.2;
  color: #454545;
  font-weight: 600;
  height: 100%;
  min-width: 100px;
  padding-bottom: 20px;
`;

const Divider = styled.div`
  width: 100%;

  height: 1px;
  background-color: #cacaca;
  margin: 10px 0;
`;

const RoundBoxStyled = styled.div`
  border-radius: 10px;
  border: 1px solid #c7c7c7;
  padding: 0px 20px;
  display: inline-block;
`;

const ApplyStyle = styled.div`
  color: #fff;
  background-color: orange;
  color: #fff;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  margin-left: 10px;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 400;
`;

const IconButtonWrapper = styled.div`
  cursor: pointer;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50px;
  width: 40px;
  height: 40px;
  &:hover {
    background: #e0e0e0;
  }
`;

export default TitleCleansingModal;
